import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { SiteNav } from '../components/SiteNav';
import { PillarLeadCapture } from '../components/PillarLeadCapture';
import { getServiceBySlug, COMPANION_ADDON } from '../data/services';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';
const fadeInUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = service.icon;

  return (
    <div className="min-h-screen bg-[#FAF8F7] text-[#2B2625]" data-testid={`service-detail-${service.slug}`}>
      <SiteNav />

      {/* Hero */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute -top-24 right-0 w-[440px] h-[440px] bg-[#E8D5D1]/50 rounded-full blur-[130px]" />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/70 border border-[#E8D5D1] rounded-full text-[#C28B87] text-sm font-semibold mb-5"><Icon className="w-4 h-4" strokeWidth={1.8} /> {service.title}</div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#2B2625] mb-5 font-medium leading-[1.05]">{service.tagline}</h1>
            <p className="text-lg text-[#6C6361] leading-relaxed mb-8 max-w-xl">{service.intro}</p>
            <Link to="/?waitlist=true"><Button className="bg-[#C28B87] text-white hover:bg-[#b07b77] rounded-full px-8 py-6 text-base font-medium shadow-[0_10px_30px_rgba(194,139,135,0.3)] transition-transform hover:scale-[1.02]" data-testid="service-detail-cta">Request Early Access <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
          </motion.div>
          <motion.div className="relative" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
            <div className="aspect-[4/3] rounded-[2.5rem] overflow-hidden shadow-[0_40px_80px_-24px_rgba(43,38,37,0.22)]"><img src={service.image} alt={service.title} className="w-full h-full object-cover" /></div>
          </motion.div>
        </div>
      </section>

      {/* Viome walkthrough */}
      {service.walkthrough && (
        <section className="py-16 lg:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <motion.div className="mb-12" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-[#C28B87] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-4">How It Works</p>
              <h2 className="font-display text-3xl sm:text-4xl text-[#2B2625] font-medium">Your Viome testing journey</h2>
            </motion.div>
            <div className="space-y-5">
              {service.walkthrough.map((step, idx) => (
                <motion.div key={idx} className="flex items-start gap-5 bg-[#FAF8F7] rounded-3xl p-6 border border-[#E8D5D1]/70" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.06 }}>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="font-display text-3xl text-[#E8D5D1]">{String(idx + 1).padStart(2, '0')}</span>
                    <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center"><step.icon className="w-6 h-6 text-[#C28B87]" strokeWidth={1.7} /></div>
                  </div>
                  <div><h3 className="font-display text-xl text-[#2B2625] font-medium">{step.title}</h3><p className="text-[#6C6361] text-sm leading-relaxed mt-1">{step.desc}</p></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Companion add-on note */}
      <section className="py-12 lg:py-16 px-6 lg:px-8 bg-gradient-to-b from-[#FAF8F7] via-[#F3EFEF]/50 to-[#FAF8F7]">
        <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-[#E8D5D1] p-6 flex items-start gap-4" data-testid="companion-addon">
          <div className="w-11 h-11 rounded-xl bg-[#F3EFEF] flex items-center justify-center flex-shrink-0"><Sparkles className="w-5 h-5 text-[#C28B87]" /></div>
          <div>
            <p className="font-medium text-[#2B2625]">Add the {COMPANION_ADDON.name}</p>
            <p className="text-sm text-[#6C6361] mt-1">{COMPANION_ADDON.desc}</p>
          </div>
        </div>
      </section>

      {/* Lead capture CTA */}
      <PillarLeadCapture pillar={service.title} slug={service.slug} />

      {/* Disclaimer + footer */}
      <footer className="bg-[#221D1C] text-white py-14 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 mb-8">
            <div className="bg-white/10 rounded-2xl px-6 py-3"><img src={LOGO_URL} alt="Rejuven" className="h-8" /></div>
            <div className="flex items-center gap-8 text-sm text-white/55"><Link to="/services" className="hover:text-white transition-colors">Services</Link><Link to="/about" className="hover:text-white transition-colors">About</Link><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></div>
            <p className="text-white/35 text-sm">© 2026 Rejuven Inc.</p>
          </div>
          <div className="border-t border-white/10 pt-6"><p className="text-white/45 text-xs leading-relaxed max-w-4xl mx-auto text-center"><strong className="text-white/60">Medical disclaimer:</strong> Viome is a third-party testing provider. Rejuven provides educational and preventative wellness information only — not medical advice, diagnosis or treatment. We do not prescribe medications, hormones or peptides. Always consult a qualified healthcare provider.</p></div>
        </div>
      </footer>
    </div>
  );
}
